// statsPanel.ts — the shared STATS modal (📊 in the Menu). Tactical OLED redesign: a full-bleed
// inset card with a divided column of lifetime retention stats (games / win-rate / best score /
// best combo / daily streak / streak-freeze), TODAY'S MISSIONS as kit listRows with a facts-learned
// counter, an ACHIEVEMENTS link, and a DONE footer. Pure UI over stats.ts + mastery.ts + the kit;
// no Core / network.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import {
  getStats,
  winRatePct,
  liveDailyStreak,
  getDailyMissions,
  getProgress,
  streakFreezeCount,
  freezeDaysToNext,
  RANKS,
  type Stats,
} from './stats';
import { factsLearnedTotal } from './mastery';
import { modal } from './ui';
import { ctaButton, secondaryButton, labelText, listRow, cardPanel, statTileRow } from './ui/kit';
import { icon, type IconName } from './icons';
import { openAchievements } from './achievementsPanel';
import { scaledFont } from './settings';
import { isLandscape } from './stage';

function streakFreezeLabel(s: Stats): { value: string; color: number } {
  const freezeN = streakFreezeCount(s);
  const freezeReady = freezeN > 0;
  return {
    value: freezeReady ? (freezeN > 1 ? `ready ×${freezeN}` : 'ready') : `next in ${freezeDaysToNext(s)}d`,
    color: freezeReady ? PALETTE.scan : PALETTE.textDim,
  };
}

export function openStats(scene: Phaser.Scene): ReturnType<typeof modal> {
  const s = getStats();
  if (isLandscape()) return openDesktopStats(scene, s);

  const progress = getProgress(s);
  const m = modal(scene, { title: 'YOUR STATS', fullBleed: true });
  const { card, cardW, bodyTop } = m;

  const rowX = -cardW / 2 + 26;
  const valX = cardW / 2 - 26;
  const labelFs = `${scaledFont(18, 13)}px`;
  const rowStep = 60;

  // One divided stat line: dim label (left) + bold value (right), an optional icon left of the
  // value, and a hairline `line` divider under the row (the redline anatomy of the reference).
  const statLine = (yy: number, label: string, value: string, color: number, iconName?: IconName): void => {
    const lbl = scene.add
      .text(rowX, yy, label, { fontFamily: FONT.family, fontSize: labelFs, color: hex(PALETTE.textDim) })
      .setOrigin(0, 0.5);
    const val = scene.add
      .text(valX, yy, value, { fontFamily: FONT.display, fontSize: labelFs, color: hex(color), fontStyle: 'bold' })
      .setOrigin(1, 0.5);
    card.add([lbl, val]);
    if (iconName) {
      const isz = scaledFont(18, 13);
      card.add(icon(scene, iconName, valX - val.width - 7 - isz / 2, yy, isz, color));
    }
    const divider = scene.add.rectangle(0, yy + rowStep / 2, cardW - 52, 1, PALETTE.line).setOrigin(0.5, 0.5);
    card.add(divider);
  };

  let y = bodyTop + 22;
  const rankH = 92;
  const rankW = cardW - 52;
  const rank = cardPanel(scene, 0, y + rankH / 2 - 4, rankW, rankH, { accent: PALETTE.scan, fill: PALETTE.surface });
  const rankNext = progress.maxed ? 'MAX RANK' : RANKS[progress.rank + 1]?.name;
  rank.add(labelText(scene, -rankW / 2 + 18, -25, 'MUNCHER RANK', { size: 11, color: PALETTE.scan }));
  rank.add(
    scene.add
      .text(-rankW / 2 + 18, 4, `LV ${progress.rank + 1} · ${progress.rankName}`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(22, 15)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );
  const xp = progress.maxed
    ? `${progress.xp.toLocaleString('en-US')} XP banked`
    : `${progress.xp.toLocaleString('en-US')} / ${progress.nextAt.toLocaleString('en-US')} XP to ${rankNext}`;
  rank.add(
    scene.add
      .text(rankW / 2 - 18, 4, xp, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5),
  );
  const bar = scene.add.graphics();
  const bw = rankW - 36;
  bar.fillStyle(PALETTE.tile, 1);
  bar.fillRoundedRect(-bw / 2, 28, bw, 8, 4);
  if (progress.pctToNext > 0) {
    bar.fillStyle(PALETTE.scan, 1);
    bar.fillRoundedRect(-bw / 2, 28, bw * progress.pctToNext, 8, 4);
  }
  rank.add(bar);
  card.add(rank);
  y += rankH + 30;

  statLine(y, 'Games played', s.gamesPlayed.toLocaleString('en-US'), PALETTE.text);
  y += rowStep;
  statLine(y, 'Win rate', `${winRatePct(s)}% · ${s.wins} wins`, PALETTE.correct);
  y += rowStep;
  statLine(y, 'Best score', s.bestScore.toLocaleString('en-US'), PALETTE.player);
  y += rowStep;
  statLine(y, 'Best combo', `×${s.bestCombo}`, PALETTE.armed);
  y += rowStep;
  statLine(y, 'Daily streak', `${liveDailyStreak(s)} (best ${s.bestDailyStreak})`, PALETTE.armed, 'flame');
  y += rowStep;
  // STREAK FREEZE (R4): "miss one day for free" safety net. ❄ + ready/used double-codes it so it's
  // legible without relying on the accent hue. Banked token ⇒ "ready"; else days kept until the next.
  const freeze = streakFreezeLabel(s);
  statLine(y, 'Streak Freeze', freeze.value, freeze.color, 'snowflake');
  y += rowStep + 16;

  // ── TODAY'S MISSIONS ── with a right-aligned facts-learned counter (P1.3 mastery collection size).
  card.add(labelText(scene, rowX, y, "TODAY'S MISSIONS", { size: 12, color: PALETTE.dim }));
  const facts = factsLearnedTotal();
  const fTxt = scene.add
    .text(valX, y, `FACTS LEARNED: ${facts.toLocaleString('en-US')}`, {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(12, 11)}px`,
      color: hex(PALETTE.scan),
      fontStyle: '600',
    })
    .setOrigin(1, 0.5);
  card.add(fTxt);
  const isz = scaledFont(14, 11);
  card.add(icon(scene, 'sparkle', valX - fTxt.width - 7 - isz / 2, y, isz, PALETTE.scan));
  y += 40;

  // The day's 3 missions as kit listRows — always reachable here even when the menu strip is hidden.
  const missions = getDailyMissions(s);
  const rowW = cardW - 52;
  for (const mv of missions) {
    const done = mv.done;
    card.add(
      listRow(scene, 0, y, rowW, {
        iconName: done ? 'checkCircle' : 'circleNotch',
        iconColor: done ? PALETTE.correct : PALETTE.mute,
        label: done ? mv.label : `${mv.label} (${mv.cur}/${mv.target})`,
        labelColor: done ? PALETTE.correct : PALETTE.text,
        value: done ? 'DONE' : `+${mv.reward}`,
        valueColor: done ? PALETTE.correct : PALETTE.armed,
        height: 48,
      }),
    );
    y += 56;
  }
  card.add(
    scene.add
      .text(0, y - 12, 'Mission XP feeds the rank bar', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(0.5),
  );
  y += 32;

  card.add(labelText(scene, rowX, y, 'CAREER TELEMETRY', { size: 12, color: PALETTE.player }));
  y += 54;
  card.add(
    statTileRow(
      scene,
      0,
      y,
      cardW - 52,
      [
        { value: s.winStreak.toLocaleString('en-US'), label: 'WIN STREAK', color: PALETTE.correct },
        { value: s.gauntletsThreaded.toLocaleString('en-US'), label: 'THREADED', color: PALETTE.player },
        { value: facts.toLocaleString('en-US'), label: 'FACTS', color: PALETTE.gold },
      ],
      { height: 76, gap: 10 },
    ),
  );
  y += 62;

  // ── footer ── ACHIEVEMENTS link + full-width DONE, flowed directly UNDER the missions list (NOT pinned
  // to the card bottom). #6 fix: the footer used to anchor at footerY (the bottom of the full-bleed card),
  // stranding a ~500px dead veil band between the missions and the buttons — and because the pinned footer
  // sat at the very bottom, the modal's auto-hug measured 0% dead band and left it. Flowing the footer with
  // the content + calling relayout() routes Stats through the SAME centred content-hug path as leaderboard/
  // today: the card shrinks to its real content and re-centres, killing the void.
  const achW = Math.min(cardW - 52, 300);
  const achY = y + 8;
  card.add(
    secondaryButton(scene, 0, achY, 'Achievements', () => openAchievements(scene), {
      width: achW,
      height: 48,
      icon: 'trophy',
      textColor: PALETTE.gold,
      fontPx: 14,
    }),
  );
  const doneY = achY + 66; // ach half-height (24) + gap (14) + done half-height (28)
  card.add(ctaButton(scene, 0, doneY, 'Done', () => m.close(), { width: cardW - 52, height: 56, fontPx: 18 }));
  // Hug + centre to the real content bottom (Done button + a small cushion), measured from bodyTop.
  m.relayout(doneY + 28 + 22 - bodyTop);

  return m;
}

function openDesktopStats(scene: Phaser.Scene, s: Stats): ReturnType<typeof modal> {
  const W = scene.scale.width;
  const progress = getProgress(s);
  const facts = factsLearnedTotal();
  const missions = getDailyMissions(s);
  const freeze = streakFreezeLabel(s);
  const m = modal(scene, {
    title: 'YOUR STATS',
    widthPx: Math.min(1128, W * 0.92),
    contentHeight: 620,
    railGrad: [PALETTE.scan, PALETTE.gold],
  });
  const { card, cardW, bodyTop, footerY } = m;
  const pad = 26;
  const gap = 22;
  const leftW = Math.round((cardW - pad * 2 - gap) * 0.39);
  const rightW = cardW - pad * 2 - gap - leftW;
  const leftX = -cardW / 2 + pad + leftW / 2;
  const rightX = leftX + leftW / 2 + gap + rightW / 2;
  const top = bodyTop + 8;

  const rankCard = cardPanel(scene, leftX, top + 98, leftW, 196, { accent: PALETTE.scan, fill: PALETTE.surface });
  rankCard.add(labelText(scene, -leftW / 2 + 22, -68, 'MUNCHER RANK', { size: 12, color: PALETTE.scan }));
  rankCard.add(
    scene.add
      .text(-leftW / 2 + 22, -22, `LV ${progress.rank + 1}`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(44, 28)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );
  rankCard.add(
    scene.add
      .text(leftW / 2 - 24, -24, progress.rankName, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(22, 15)}px`,
        color: hex(PALETTE.gold),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5),
  );
  const bar = scene.add.graphics();
  const barW = leftW - 48;
  bar.fillStyle(PALETTE.tile, 1);
  bar.fillRoundedRect(-barW / 2, 28, barW, 10, 5);
  bar.fillStyle(PALETTE.scan, 1);
  bar.fillRoundedRect(-barW / 2, 28, Math.max(10, barW * progress.pctToNext), 10, 5);
  rankCard.add(bar);
  const nextRank = progress.maxed ? 'MAX RANK' : RANKS[progress.rank + 1]?.name;
  const xpLine = progress.maxed
    ? `${progress.xp.toLocaleString('en-US')} XP BANKED`
    : `${progress.xp.toLocaleString('en-US')} / ${progress.nextAt.toLocaleString('en-US')} XP`;
  rankCard.add(
    scene.add
      .text(-leftW / 2 + 24, 60, xpLine, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );
  rankCard.add(
    scene.add
      .text(leftW / 2 - 24, 60, progress.maxed ? 'LOCKED IN' : `NEXT: ${nextRank}`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5),
  );
  card.add(rankCard);

  card.add(
    statTileRow(
      scene,
      leftX,
      top + 244,
      leftW,
      [
        { value: s.gamesPlayed.toLocaleString('en-US'), label: 'PLAYED', color: PALETTE.text },
        { value: `${winRatePct(s)}%`, label: 'WIN RATE', color: PALETTE.correct },
      ],
      { height: 74, gap: 12 },
    ),
  );
  card.add(
    statTileRow(
      scene,
      leftX,
      top + 330,
      leftW,
      [
        { value: s.bestScore.toLocaleString('en-US'), label: 'BEST SCORE', color: PALETTE.player },
        { value: `×${s.bestCombo}`, label: 'BEST COMBO', color: PALETTE.armed },
      ],
      { height: 74, gap: 12 },
    ),
  );

  const signalCard = cardPanel(scene, leftX, top + 438, leftW, 118, { accent: PALETTE.correct, fill: PALETTE.surface });
  signalCard.add(labelText(scene, -leftW / 2 + 22, -36, 'HABIT SIGNAL', { size: 12, color: PALETTE.correct }));
  signalCard.add(icon(scene, 'flame', -leftW / 2 + 34, 4, 24, PALETTE.armed));
  signalCard.add(
    scene.add
      .text(-leftW / 2 + 56, -2, `${liveDailyStreak(s)} day streak`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(20, 14)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );
  signalCard.add(
    scene.add
      .text(-leftW / 2 + 56, 28, `Best ${s.bestDailyStreak} · Freeze ${freeze.value}`, {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(freeze.color),
        fontStyle: '600',
      })
      .setOrigin(0, 0.5),
  );
  card.add(signalCard);

  const missionCard = cardPanel(scene, rightX, top + 150, rightW, 300, { accent: PALETTE.gold, fill: PALETTE.surface });
  missionCard.add(labelText(scene, -rightW / 2 + 22, -116, "TODAY'S MISSIONS", { size: 12, color: PALETTE.gold }));
  missionCard.add(
    scene.add
      .text(rightW / 2 - 22, -116, `FACTS LEARNED: ${facts.toLocaleString('en-US')}`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5),
  );
  let missionY = -62;
  for (const mv of missions) {
    missionCard.add(
      listRow(scene, 0, missionY, rightW - 36, {
        iconName: mv.done ? 'checkCircle' : 'circleNotch',
        iconColor: mv.done ? PALETTE.correct : PALETTE.mute,
        label: mv.done ? mv.label : `${mv.label} (${mv.cur}/${mv.target})`,
        labelColor: mv.done ? PALETTE.correct : PALETTE.text,
        value: mv.done ? 'DONE' : `+${mv.reward}`,
        valueColor: mv.done ? PALETTE.correct : PALETTE.armed,
        height: 52,
      }),
    );
    missionY += 62;
  }
  missionCard.add(
    scene.add
      .text(0, 118, 'MISSION XP FEEDS THE RANK BAR', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(0.5),
  );
  card.add(missionCard);

  const masteryCard = cardPanel(scene, rightX, top + 420, rightW, 174, { accent: PALETTE.player, fill: PALETTE.surface });
  masteryCard.add(labelText(scene, -rightW / 2 + 22, -58, 'CAREER TELEMETRY', { size: 12, color: PALETTE.player }));
  masteryCard.add(
    statTileRow(
      scene,
      0,
      12,
      rightW - 36,
      [
        { value: s.winStreak.toLocaleString('en-US'), label: 'WIN STREAK', color: PALETTE.correct },
        { value: s.gauntletsThreaded.toLocaleString('en-US'), label: 'THREADED', color: PALETTE.player },
        { value: facts.toLocaleString('en-US'), label: 'FACTS', color: PALETTE.gold },
      ],
      { height: 86, gap: 10 },
    ),
  );
  card.add(masteryCard);

  const btnW = (cardW - pad * 2 - gap) / 2;
  card.add(
    secondaryButton(scene, -btnW / 2 - gap / 2, footerY, 'Achievements', () => openAchievements(scene), {
      width: btnW,
      height: 56,
      icon: 'trophy',
      textColor: PALETTE.gold,
      fontPx: 15,
    }),
  );
  card.add(ctaButton(scene, btnW / 2 + gap / 2, footerY, 'Done', () => m.close(), { width: btnW, height: 56, fontPx: 18 }));

  return m;
}
